import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { accountsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Target as TargetIcon,
  Save,
  X,
  Pencil,
  Trash2,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import InlineIntegrityChip from '../components/InlineIntegrityChip';
import { format } from 'date-fns';

const monthLabel = (m) => {
  try { return format(new Date(`${m}-01T00:00:00`), 'MMMM yyyy'); }
  catch (_e) { return m; }
};

const fmtINR = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return `\u20B9${Number(v).toLocaleString('en-IN')}`;
};

const TARGET_FIELDS = [
  { key: 'net_sales', label: 'Net Sales (₹)', step: '1000', placeholder: 'e.g. 2000000' },
  { key: 'food_cost_pct', label: 'Food Cost %', step: '0.5', placeholder: 'e.g. 30' },
  { key: 'gross_margin_pct', label: 'Gross Margin %', step: '0.5', placeholder: 'e.g. 70' },
  { key: 'labor_cost_pct', label: 'Labor Cost %', step: '0.5', placeholder: 'e.g. 18' },
];

export default function TargetsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingMonth, setEditingMonth] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const outletId = user?.outlet_id || '';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await accountsAPI.listTargets({ months: 12 });
      setRows(res.data?.months || []);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to load targets';
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEditor = (row) => {
    setEditingMonth(row.month);
    setDraft({
      net_sales: row.targets?.net_sales ?? '',
      food_cost_pct: row.targets?.food_cost_pct ?? '',
      gross_margin_pct: row.targets?.gross_margin_pct ?? '',
      labor_cost_pct: row.targets?.labor_cost_pct ?? '',
    });
  };

  const cancelEditor = () => {
    setEditingMonth(null);
    setDraft({});
  };

  const save = async () => {
    if (!editingMonth) return;
    const targets = {};
    TARGET_FIELDS.forEach((f) => {
      const raw = draft[f.key];
      if (raw !== '' && raw !== null && raw !== undefined && !isNaN(Number(raw))) {
        targets[f.key] = Number(raw);
      }
    });
    if (Object.keys(targets).length === 0) {
      toast.error('Enter at least one target value before saving.');
      return;
    }
    setSaving(true);
    try {
      await accountsAPI.upsertTarget({ month: editingMonth, targets });
      toast.success(`Targets saved for ${monthLabel(editingMonth)}`);
      cancelEditor();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save targets');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete targets for ${monthLabel(row.month)}? This cannot be undone.`)) return;
    try {
      await accountsAPI.deleteTarget(row.month);
      toast.success(`Targets cleared for ${monthLabel(row.month)}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete targets');
    }
  };

  const driftCount = useMemo(
    () => rows.filter((r) => r.has_target && r.canonical_synced === false).length,
    [rows],
  );

  return (
    <div className="space-y-6" data-testid="targets-page">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-100 flex items-center gap-2">
            <TargetIcon className="w-5 h-5 text-amber-400" />
            Monthly Targets
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Set the monthly revenue, cost, and margin targets that drive P&L pacing, the
            Target Variance strip, and the Operating Forecast.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <InlineIntegrityChip surface="targets" />
        </div>
      </div>

      {/* Outlet context */}
      <Card className="bg-zinc-900/60 border-zinc-800" data-testid="targets-context-card">
        <CardContent className="py-3 flex flex-wrap items-center gap-4 text-xs">
          <div>
            <span className="text-zinc-500">Outlet</span>
            <span className="ml-2 font-mono text-zinc-300" data-testid="targets-outlet-id">
              {outletId || 'unknown'}
            </span>
          </div>
          <div>
            <span className="text-zinc-500">Organization</span>
            <span className="ml-2 text-zinc-300">{user?.org_name || '—'}</span>
          </div>
          {driftCount > 0 && (
            <div className="ml-auto flex items-center gap-1.5 text-amber-300" data-testid="targets-drift-warning">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>{driftCount} month{driftCount === 1 ? '' : 's'} have canonical drift</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm text-zinc-300 font-medium">Last 12 months</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
            </div>
          ) : error ? (
            <div
              className="flex items-start gap-2 rounded-md border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-xs text-rose-200"
              data-testid="targets-load-error"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 text-rose-400" />
              <div>
                <p className="font-medium">Unable to load targets</p>
                <p className="opacity-80">{error}</p>
              </div>
              <Button size="sm" variant="outline" className="ml-auto text-xs" onClick={load} data-testid="targets-retry-btn">
                Retry
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800/60">
              {rows.map((row) => {
                const isEditing = editingMonth === row.month;
                const t = row.targets || {};
                return (
                  <div key={row.month} className="py-3" data-testid={`target-row-${row.month}`}>
                    {/* Row header */}
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="min-w-[8rem]">
                        <p className="text-sm text-zinc-200 font-medium">{monthLabel(row.month)}</p>
                        <p className="text-[10px] text-zinc-500 font-mono">{row.month}</p>
                      </div>
                      {row.has_target ? (
                        <>
                          <span className="text-sm font-mono text-zinc-200" data-testid={`target-net-sales-${row.month}`}>
                            {fmtINR(t.net_sales)}
                          </span>
                          {t.food_cost_pct ? <Badge className="text-[10px] bg-zinc-800 text-zinc-300">Food {t.food_cost_pct}%</Badge> : null}
                          {t.gross_margin_pct ? <Badge className="text-[10px] bg-zinc-800 text-zinc-300">GM {t.gross_margin_pct}%</Badge> : null}
                          {t.labor_cost_pct ? <Badge className="text-[10px] bg-zinc-800 text-zinc-300">Labor {t.labor_cost_pct}%</Badge> : null}
                          {row.canonical_synced === false && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-amber-300" title="Legacy and canonical target rows disagree">
                              <AlertTriangle className="w-3 h-3" /> canonical drift
                            </span>
                          )}
                          {row.canonical_synced === true && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400/70" title="Legacy and canonical rows match">
                              <CheckCircle2 className="w-3 h-3" /> synced
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-zinc-500 italic" data-testid={`target-empty-${row.month}`}>
                          No target set
                        </span>
                      )}
                      <div className="ml-auto flex items-center gap-2">
                        {!isEditing && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7"
                            onClick={() => openEditor(row)}
                            data-testid={`target-edit-btn-${row.month}`}
                          >
                            <Pencil className="w-3 h-3 mr-1" />
                            {row.has_target ? 'Edit' : 'Set'}
                          </Button>
                        )}
                        {row.has_target && !isEditing && user?.is_org_admin && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs h-7 text-rose-300 hover:text-rose-200 hover:bg-rose-500/10"
                            onClick={() => remove(row)}
                            data-testid={`target-delete-btn-${row.month}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Inline editor */}
                    {isEditing && (
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 rounded-md border border-zinc-800/80 bg-zinc-950/40 p-3" data-testid={`target-editor-${row.month}`}>
                        {TARGET_FIELDS.map((f) => (
                          <div key={f.key}>
                            <Label className="text-[10px] text-zinc-500">{f.label}</Label>
                            <Input
                              type="number"
                              step={f.step}
                              value={draft[f.key]}
                              onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                              className="h-8 bg-zinc-800 border-zinc-700 text-xs"
                              placeholder={f.placeholder}
                              data-testid={`target-input-${f.key}-${row.month}`}
                            />
                          </div>
                        ))}
                        <div className="col-span-2 md:col-span-4 flex items-center justify-end gap-2">
                          <Button size="sm" variant="ghost" className="text-xs" onClick={cancelEditor}>
                            <X className="w-3 h-3 mr-1" /> Cancel
                          </Button>
                          <Button
                            size="sm"
                            className="bg-amber-500 hover:bg-amber-600 text-black text-xs"
                            onClick={save}
                            disabled={saving}
                            data-testid={`target-save-btn-${row.month}`}
                          >
                            <Save className="w-3 h-3 mr-1" />
                            {saving ? 'Saving…' : 'Save'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Helper note */}
      <p className="text-[11px] text-zinc-500" data-testid="targets-help-note">
        Targets are scoped to the current outlet ({outletId ? outletId.slice(0, 8) : 'unknown'}).
        Saving updates both the legacy <code>monthly_targets</code> collection and the canonical{' '}
        <code>targets</code> collection so the P&amp;L Target Variance strip and the Operating
        Forecast read the same number.
      </p>
    </div>
  );
}
